import { useCallback, useMemo, useState } from "react"
import { GALAXY_CONTROLS_CONSTANTS } from "@/app/playbook/galaxies/utils/galaxyControlsConstants"

const galaxyDesktop = GALAXY_CONTROLS_CONSTANTS.galaxyMode.desktop

// Fallback boundary when the end-cap nav grid isn't in the DOM (see below):
// viewport fraction (from the top) the Epilogue header must cross before the
// HUD enters epilogue mode and the intermediate background dims.
const EPILOGUE_SCROLLED_TO_VIEWPORT_FRACTION = 0.5

export const useGalaxy = (
  contentSectionRef: React.RefObject<HTMLDivElement | null>,
  isEpilogueExpanded: boolean
) => {
  const [galaxyProgress, setGalaxyProgress] = useState(0)
  // True while the Epilogue button (the end-cap nav grid the reader clicked)
  // is on screen or scrolled off the TOP — i.e. from the moment the epilogue
  // opens (the click leaves the grid near the top of the viewport). Drives the
  // Earth-centered HUD mode and the intermediate background dim. Scrolling up
  // far enough that the grid sinks below the viewport bottom flips it false,
  // which plays the whole HUD transition in reverse. Reset whenever the
  // epilogue collapses.
  const [isEpilogueScrolledTo, setIsEpilogueScrolledTo] = useState(false)
  // The Earth→starfield handoff begins once the reader scrolls the Epilogue
  // header to the viewport center (progress > 0): the HUD globe crossfades
  // out while the galaxy camera starts its pull-back from the near-Earth
  // keyframe. Above the header (progress 0) the globe is back and the galaxy
  // is hidden, so the boundary is symmetric on scroll-up.
  const showGalaxy = useMemo(
    () => isEpilogueExpanded && galaxyProgress > 0,
    [galaxyProgress, isEpilogueExpanded]
  )

  const updateGalaxyProgressFromScroll = useCallback(() => {
    if (!isEpilogueExpanded || !contentSectionRef.current) {
      setGalaxyProgress(0)
      setIsEpilogueScrolledTo(false)
      return
    }
    const elTop = contentSectionRef.current.offsetTop
    const elHeight = contentSectionRef.current.offsetHeight
    if (elHeight <= 0) return
    const galaxyStartEl = contentSectionRef.current.querySelector(
      '[data-playbook-galaxy-start="true"]'
    ) as HTMLElement | null
    if (!galaxyStartEl) {
      setGalaxyProgress(0)
      setIsEpilogueScrolledTo(false)
      return
    }
    // Epilogue HUD mode tracks the end-cap nav grid (the Epilogue button's
    // home): in view or above the viewport ⇒ mode on, sunk below the viewport
    // bottom (reader scrolled back up past it) ⇒ mode off. The deep-link
    // scroll on click lands the grid near the top, so the mode engages
    // immediately on click. Fall back to the Epilogue-header boundary where
    // the grid isn't rendered (e.g. inline continuations without the hub cap).
    const navGridEl = contentSectionRef.current.querySelector(
      '[data-epilogue-nav-grid="true"]'
    ) as HTMLElement | null
    const galaxyStartTop = galaxyStartEl.getBoundingClientRect().top
    setIsEpilogueScrolledTo(
      navGridEl
        ? navGridEl.getBoundingClientRect().top < window.innerHeight
        : galaxyStartTop <= window.innerHeight * EPILOGUE_SCROLLED_TO_VIEWPORT_FRACTION
    )
    const galaxyStartOffset = (galaxyStartEl.offsetTop - elTop) / elHeight
    const currentScrollOffset = (window.scrollY + window.innerHeight / 2 - elTop) / elHeight
    // Piecewise scroll→progress mapping, pinned to the epilogue's dateMarkers
    // so the zoom's acts land on the prose regardless of paragraph lengths:
    //   header (2041)  → progress 0     empty sky behind the globe
    //   2042 marker    → stars anchor   HUD globe zooms away + fades out;
    //                                   the starfield fades in just after
    //   2043 marker    → galaxies anchor  Milky Way starts materializing
    //   content end    → progress 1
    // The star/galaxy fade thresholds sit at (Milky Way) or just past (stars)
    // their anchor progress values — see GALAXY_CONTROLS_CONSTANTS'
    // fadeThresholds — so each beat lands on its paragraph. Missing markers
    // drop out of the anchor list, degrading gracefully to a linear ramp.
    const galaxyStartRectTop = galaxyStartEl.getBoundingClientRect().top
    const markerScrollOffset = (dateString: string) => {
      const markerEl = galaxyStartEl.querySelector(`[data-chart-date="${dateString}"]`)
      if (!markerEl) return null
      return galaxyStartOffset
        + (markerEl.getBoundingClientRect().top - galaxyStartRectTop) / elHeight
    }
    const { starsAnchorProgress, galaxiesAnchorProgress } = GALAXY_CONTROLS_CONSTANTS.scrollAnchors
    const anchors: [scrollOffset: number, progress: number][] = [[galaxyStartOffset, 0]]
    const starsScrollOffset = markerScrollOffset("2042-01-01")
    if (starsScrollOffset !== null && starsScrollOffset > anchors[anchors.length - 1][0]) {
      anchors.push([starsScrollOffset, starsAnchorProgress])
    }
    const galaxiesScrollOffset = markerScrollOffset("2043-01-01")
    if (galaxiesScrollOffset !== null && galaxiesScrollOffset > anchors[anchors.length - 1][0]) {
      anchors.push([galaxiesScrollOffset, galaxiesAnchorProgress])
    }
    if (anchors[anchors.length - 1][0] < 1) anchors.push([1, 1])
    if (currentScrollOffset < galaxyStartOffset) {
      setGalaxyProgress(0)
      return
    }
    let progress = 1
    for (let i = 0; i < anchors.length - 1; i++) {
      const [segmentStart, progressStart] = anchors[i]
      const [segmentEnd, progressEnd] = anchors[i + 1]
      if (currentScrollOffset <= segmentEnd) {
        progress = progressStart
          + (progressEnd - progressStart)
            * ((currentScrollOffset - segmentStart) / (segmentEnd - segmentStart))
        break
      }
    }
    setGalaxyProgress(Math.max(0, Math.min(1, progress)))
  }, [contentSectionRef, isEpilogueExpanded])

  return {
    showGalaxy,
    isEpilogueScrolledTo,
    galaxyProgress,
    updateGalaxyProgressFromScroll,
    galaxyFadeInDurationMs: galaxyDesktop.galaxyFadeInDurationMs,
  }
}
